import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, ChevronRight, Plus, Minus, Clock, Calendar, Mail, ListTree } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import CookieBanner from "@/components/CookieBanner";
import { useSeo } from "@/hooks/useSeo";
import { ARTICLE_META, INTRO, SECTIONS, FAQS } from "@/pages/blogComprarDatos";

const WA = "https://wa.me/56936400479?text=Hola%2C%20le%C3%AD%20el%20art%C3%ADculo%20sobre%20comprar%20datos%20de%20contactos%20y%20quiero%20cotizar%20una%20base%20personalizada";

const Bold = ({ x }) => {
  const parts = x.split("**");
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <strong key={i} className="text-white font-semibold">{p}</strong>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  );
};

const CtaBox = () => (
  <div
    data-testid="blog-inline-cta"
    className="my-10 rounded-2xl border border-[rgba(37,211,102,0.35)] bg-[rgba(37,211,102,0.06)] p-7 sm:p-8"
  >
    <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#25D366] mb-3">
      Cotiza tu base personalizada
    </div>
    <h3 className="font-display font-bold text-white text-xl sm:text-2xl leading-tight mb-3">
      ¿Necesitas contactos de empresas segmentados por rubro y región?
    </h3>
    <p className="text-[#94a3b8] text-[15px] leading-relaxed mb-6">
      En ChileDatosPro preparamos bases de datos de empresas chilenas filtradas
      según tu mercado objetivo, en cumplimiento con la Ley 21.719. Cuéntanos
      qué necesitas y te enviamos una cotización rápida.
    </p>
    <a
      href={WA}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="blog-inline-cta-whatsapp"
      className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold text-[15px] px-7 py-3.5 rounded-full transition-all shadow-[0_12px_40px_-8px_rgba(37,211,102,0.5)] hover:-translate-y-0.5"
    >
      <MessageCircle size={18} />
      <span>Cotizar por WhatsApp</span>
    </a>
  </div>
);

const EmailExample = () => (
  <div className="my-8 rounded-2xl border border-[rgba(0,240,255,0.25)] bg-[rgba(10,18,36,0.6)] overflow-hidden">
    <div className="flex items-center gap-3 px-6 py-4 border-b border-[rgba(0,240,255,0.15)] bg-[rgba(0,240,255,0.04)]">
      <Mail className="text-[#00f0ff]" size={16} />
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#00f0ff]">
        Asunto: Base de empresas para su equipo comercial
      </span>
    </div>
    <div className="px-6 py-6 text-[14.5px] text-[#cbd5e1] leading-relaxed space-y-4">
      <p>Hola, [Nombre]:</p>
      <p>
        Te contacto porque ayudamos a empresas que necesitan encontrar nuevos
        clientes sin dedicar semanas a buscar contactos manualmente.
      </p>
      <p>
        En ChileDatosPro puedes solicitar bases de empresas segmentadas por
        rubro, región, comuna, tamaño y otros criterios, recibiendo solamente
        los registros que se ajusten a tu mercado.
      </p>
      <p>
        Esto permite que el equipo comercial dedique más tiempo a contactar
        prospectos, realizar seguimiento y generar ventas.
      </p>
      <p>
        ¿Te interesa que preparemos una muestra o una cotización según el tipo
        de empresa que necesitas contactar?
      </p>
      <p className="text-[#94a3b8]">
        Saludos,
        <br />
        [Nombre] · [Cargo]
        <br />
        <span className="text-[#00f0ff]">ChileDatosPro.cl</span>
      </p>
    </div>
  </div>
);

const Block = ({ b }) => {
  if (b.t === "p")
    return (
      <p className="text-[15.5px] text-[#94a3b8] leading-[1.85] mb-5">
        <Bold x={b.x} />
      </p>
    );
  if (b.t === "h3")
    return (
      <h3 className="font-display font-bold text-white text-lg sm:text-xl mt-8 mb-4">
        {b.x}
      </h3>
    );
  if (b.t === "ul")
    return (
      <ul className="space-y-2.5 mb-6 pl-1">
        {b.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-[15px] text-[#94a3b8] leading-relaxed">
            <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-[#00f0ff] shrink-0 shadow-[0_0_6px_rgba(0,240,255,0.8)]" />
            <span><Bold x={it} /></span>
          </li>
        ))}
      </ul>
    );
  if (b.t === "ol")
    return (
      <ol className="space-y-3 mb-6">
        {b.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-[15px] text-[#94a3b8] leading-relaxed">
            <span className="shrink-0 w-6 h-6 rounded-full border border-[rgba(0,240,255,0.4)] text-[#00f0ff] font-mono text-[11px] flex items-center justify-center mt-0.5">
              {i + 1}
            </span>
            <span><Bold x={it} /></span>
          </li>
        ))}
      </ol>
    );
  if (b.t === "quote")
    return (
      <blockquote className="my-6 pl-5 border-l-2 border-[#00f0ff] text-[15px] text-[#cbd5e1] italic leading-relaxed">
        {b.x}
      </blockquote>
    );
  if (b.t === "grid")
    return (
      <div className="grid sm:grid-cols-2 gap-4 my-6">
        {b.items.map(([title, desc]) => (
          <div key={title} className="rounded-xl border border-[rgba(0,240,255,0.15)] bg-[rgba(10,18,36,0.4)] p-5">
            <h3 className="font-display font-semibold text-white text-[15px] mb-2">{title}</h3>
            <p className="text-[13.5px] text-[#94a3b8] leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    );
  if (b.t === "email") return <EmailExample />;
  if (b.t === "cta") return <CtaBox />;
  return null;
};

const FaqAccordion = () => {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3">
      {FAQS.map((f, i) => {
        const isOpen = open === i;
        return (
          <div
            key={f.q}
            data-testid={`blog-faq-item-${i}`}
            className={`rounded-2xl border transition-all overflow-hidden ${
              isOpen
                ? "border-[rgba(0,240,255,0.5)] bg-[rgba(0,240,255,0.04)]"
                : "border-[rgba(0,240,255,0.12)] bg-[rgba(10,18,36,0.4)]"
            }`}
          >
            <button
              onClick={() => setOpen(isOpen ? -1 : i)}
              className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
              data-testid={`blog-faq-trigger-${i}`}
            >
              <span className="font-display font-semibold text-white text-[15px] sm:text-base leading-tight">
                {f.q}
              </span>
              <div
                className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 transition-all ${
                  isOpen
                    ? "bg-[#00f0ff] border-[#00f0ff] text-[#040812]"
                    : "border-[rgba(0,240,255,0.3)] text-[#00f0ff]"
                }`}
              >
                {isOpen ? <Minus size={14} /> : <Plus size={14} />}
              </div>
            </button>
            {isOpen && (
              <div className="px-6 pb-6 text-[#94a3b8] text-[14px] leading-relaxed">
                {f.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "Comprar datos de contactos para empresas en Chile: ventajas, ahorro y email frío",
    description: ARTICLE_META.description,
    inLanguage: "es-CL",
    datePublished: ARTICLE_META.datePublished,
    dateModified: ARTICLE_META.datePublished,
    mainEntityOfPage: { "@type": "WebPage", "@id": ARTICLE_META.canonical },
    image: "https://chiledatospro.cl/og-image.png",
    author: { "@type": "Organization", name: "Chile Datos Pro", url: "https://chiledatospro.cl/" },
    publisher: {
      "@type": "Organization",
      name: "Chile Datos Pro",
      logo: { "@type": "ImageObject", url: "https://chiledatospro.cl/icon-512.png" },
    },
    keywords: "comprar datos de contactos, comprar base de datos de empresas en Chile, contactos de empresas chilenas, base de datos personalizada, email frío para empresas, prospección comercial B2B",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: "https://chiledatospro.cl/" },
      { "@type": "ListItem", position: 2, name: "Comprar datos de contactos", item: ARTICLE_META.canonical },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
];

export default function BlogComprarDatos() {
  useSeo(ARTICLE_META);
  const [tocOpen, setTocOpen] = useState(false);

  return (
    <div className="App relative">
      {jsonLd.map((s, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(s) }} />
      ))}
      <Navbar />
      <main data-testid="blog-article-page">
        {/* Hero */}
        <header className="relative pt-32 pb-14 overflow-hidden border-b border-[rgba(0,240,255,0.08)]">
          <div className="absolute inset-0 grid-pattern opacity-50" />
          <div className="absolute inset-0 radial-glow" />
          <div className="relative max-w-4xl mx-auto px-5 sm:px-8">
            <nav aria-label="breadcrumb" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#64748b] mb-6">
              <Link to="/" className="hover:text-[#00f0ff] transition-colors" data-testid="blog-breadcrumb-home">Inicio</Link>
              <ChevronRight size={12} />
              <span className="text-[#00f0ff]">Blog</span>
            </nav>
            <h1 className="font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight mb-6">
              Comprar datos de contactos: cómo{" "}
              <span className="text-[#00f0ff]">ahorrar tiempo</span>, reducir
              costos y encontrar clientes potenciales de forma más eficiente
            </h1>
            <div className="flex flex-wrap items-center gap-5 text-[#94a3b8] font-mono text-[11px] uppercase tracking-wider">
              <span className="flex items-center gap-2"><Calendar size={13} className="text-[#00f0ff]" /> Julio 2026</span>
              <span className="flex items-center gap-2"><Clock size={13} className="text-[#00f0ff]" /> {ARTICLE_META.readingTime}</span>
              <span className="px-3 py-1 rounded-full border border-[rgba(0,240,255,0.3)] text-[#00f0ff]">Prospección B2B</span>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12">
          {/* Intro */}
          <div className="mb-10">
            {INTRO.map((p, i) => (
              <p key={i} className={`leading-[1.85] mb-5 ${i === 0 ? "text-[17px] text-[#cbd5e1]" : "text-[15.5px] text-[#94a3b8]"}`}>
                <Bold x={p} />
              </p>
            ))}
          </div>

          {/* TOC */}
          <div className="mb-12 rounded-2xl border border-[rgba(0,240,255,0.15)] bg-[rgba(10,18,36,0.4)] overflow-hidden">
            <button
              onClick={() => setTocOpen(!tocOpen)}
              data-testid="blog-toc-toggle"
              className="w-full flex items-center justify-between px-6 py-4 text-left"
            >
              <span className="flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.2em] text-[#00f0ff]">
                <ListTree size={16} /> Contenido del artículo
              </span>
              {tocOpen ? <Minus size={16} className="text-[#00f0ff]" /> : <Plus size={16} className="text-[#00f0ff]" />}
            </button>
            {tocOpen && (
              <ol className="px-6 pb-6 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                {SECTIONS.map((s, i) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="text-[13px] text-[#94a3b8] hover:text-[#00f0ff] transition-colors leading-snug block py-0.5">
                      {i + 1}. {s.h2}
                    </a>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* Sections */}
          <article>
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-28 mb-12">
                <h2 className="font-display font-black text-white text-2xl sm:text-3xl leading-tight tracking-tight mb-6">
                  {s.h2}
                </h2>
                {s.blocks.map((b, i) => (
                  <Block key={i} b={b} />
                ))}
              </section>
            ))}
          </article>

          {/* Final CTA */}
          <div
            data-testid="blog-final-cta"
            className="my-14 rounded-3xl gradient-border relative overflow-hidden p-8 sm:p-12 text-center"
          >
            <div className="absolute inset-0 radial-glow pointer-events-none" />
            <div className="relative">
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#00f0ff] mb-4">
                Llamado a la acción
              </div>
              <h2 className="font-display font-black text-white text-2xl sm:text-4xl leading-tight mb-4">
                Encuentra los contactos que tu empresa necesita
              </h2>
              <p className="text-[#94a3b8] text-[15.5px] leading-relaxed max-w-2xl mx-auto mb-8">
                Ahorra horas de búsqueda y crea una base de prospectos según tu
                mercado objetivo. En ChileDatosPro.cl puedes solicitar datos de
                empresas y contactos segmentados por rubro, región, comuna,
                tamaño y otros criterios disponibles. Compra solamente los datos
                que necesitas y comienza a prospectar de manera más eficiente.
              </p>
              <a
                href={WA}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="blog-final-cta-whatsapp"
                className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold text-base px-8 py-4 rounded-full transition-all shadow-[0_12px_40px_-8px_rgba(37,211,102,0.5)] hover:-translate-y-0.5"
              >
                <MessageCircle size={20} />
                <span>Solicitar una base personalizada</span>
              </a>
            </div>
          </div>

          {/* FAQ */}
          <section id="preguntas-frecuentes" className="scroll-mt-28">
            <h2 className="font-display font-black text-white text-2xl sm:text-3xl leading-tight tracking-tight mb-8">
              Preguntas frecuentes sobre comprar datos de contactos
            </h2>
            <FaqAccordion />
          </section>
        </div>
      </main>
      <Footer />
      <FloatingWhatsapp />
      <CookieBanner />
    </div>
  );
}
